import { useEffect, useState } from 'react';
import { Button, Input, InputNumber, Empty, Typography, Modal, Table, Form, Select, Tag, Card, Drawer, Popconfirm, Space } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, ArrowLeftOutlined, AppstoreOutlined, TagOutlined, MinusCircleOutlined, HistoryOutlined } from '@ant-design/icons';
import { useSingleService } from './SingleServiceStore';

const { Text, Title } = Typography;

function Warehouse({ serviceId }) {
    const {
        warehouse, getWarehouse, addWarehouse, updateWarehouse, deleteWarehouse,
        warehouseItems, getWarehouseItems, addWarehouseItem, updateWarehouseItem, deleteWarehouseItem,
        warehouseCategories, getWarehouseCategories, addWarehouseCategory, updateWarehouseCategory, deleteWarehouseCategory,
        warehouseWriteoffs, getWarehouseWriteoffs, writeoffItem,
    } = useSingleService();

    const [modalOpen, setModalOpen] = useState(false);
    const [editRecord, setEditRecord] = useState(null);
    const [form] = Form.useForm();

    // Items
    const [activeWarehouse, setActiveWarehouse] = useState(null);
    const [itemModal, setItemModal] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [itemForm] = Form.useForm();

    // Categories
    const [catDrawer, setCatDrawer] = useState(false);
    const [catModal, setCatModal] = useState(false);
    const [editCat, setEditCat] = useState(null);
    const [catForm] = Form.useForm();

    // Writeoff (spisanie)
    const [writeoffModal, setWriteoffModal] = useState(false);
    const [writeoffRecord, setWriteoffRecord] = useState(null);
    const [writeoffForm] = Form.useForm();
    const [historyDrawer, setHistoryDrawer] = useState(false);

    useEffect(() => {
        if (serviceId) getWarehouse(serviceId);
    }, [serviceId, getWarehouse]);

    useEffect(() => {
        if (activeWarehouse) {
            getWarehouseItems(activeWarehouse.id);
            getWarehouseCategories(activeWarehouse.id);
            getWarehouseWriteoffs(activeWarehouse.id);
        }
    }, [activeWarehouse, getWarehouseItems, getWarehouseCategories, getWarehouseWriteoffs]);

    // === WAREHOUSE CRUD ===
    const openAddWarehouse = () => {
        setEditRecord(null);
        form.resetFields();
        setModalOpen(true);
    };
    const openEditWarehouse = (record) => {
        setEditRecord(record);
        form.setFieldsValue(record);
        setModalOpen(true);
    };
    const handleWarehouseSubmit = (values) => {
        values.services_id = serviceId;
        const business = JSON.parse(localStorage.getItem('mybusiness'));
        values.bussiness_id = business?.id;
        const promise = editRecord
            ? updateWarehouse(editRecord.id, { ...values, id: editRecord.id })
            : addWarehouse(values);
        promise?.then(() => {
            setModalOpen(false);
            form.resetFields();
        });
    };
    const handleDeleteWarehouse = (record) => {
        Modal.confirm({
            title: "Omborni o'chirishni tasdiqlang",
            content: "Ombor ichidagi barcha kategoriya va mahsulotlar ham o'chiriladi!",
            okText: "O'chirish", okType: 'danger', cancelText: 'Bekor qilish',
            onOk: () => {
                deleteWarehouse(record.id, serviceId);
                if (activeWarehouse?.id === record.id) setActiveWarehouse(null);
            },
        });
    };

    // === ITEM CRUD ===
    const openAddItem = () => {
        setEditItem(null);
        itemForm.resetFields();
        setItemModal(true);
    };
    const openEditItem = (record) => {
        setEditItem(record);
        itemForm.setFieldsValue(record);
        setItemModal(true);
    };
    const handleItemSubmit = (values) => {
        const wId = activeWarehouse.id;
        const promise = editItem
            ? updateWarehouseItem(wId, editItem.id, values)
            : addWarehouseItem(wId, values);
        promise?.then(() => {
            setItemModal(false);
            itemForm.resetFields();
        });
    };
    const handleDeleteItem = (record) => {
        Modal.confirm({
            title: "Mahsulotni o'chirishni tasdiqlang",
            okText: "O'chirish", okType: 'danger', cancelText: 'Bekor qilish',
            onOk: () => deleteWarehouseItem(activeWarehouse.id, record.id),
        });
    };

    // === WRITEOFF ===
    const openWriteoff = (record) => {
        setWriteoffRecord(record);
        writeoffForm.resetFields();
        setWriteoffModal(true);
    };
    const handleWriteoff = (values) => {
        const wId = activeWarehouse.id;
        writeoffItem(wId, {
            warehouse_items_id: writeoffRecord.id,
            count: values.count,
            reason: values.reason,
        })?.then(() => {
            setWriteoffModal(false);
            writeoffForm.resetFields();
        }).catch(() => {});
    };

    // === CATEGORY CRUD ===
    const openAddCat = () => {
        setEditCat(null);
        catForm.resetFields();
        setCatModal(true);
    };
    const openEditCat = (cat) => {
        setEditCat(cat);
        catForm.setFieldsValue(cat);
        setCatModal(true);
    };
    const handleCatSubmit = (values) => {
        const wId = activeWarehouse.id;
        const promise = editCat
            ? updateWarehouseCategory(wId, editCat.id, values)
            : addWarehouseCategory(wId, values);
        promise?.then(() => {
            setCatModal(false);
            catForm.resetFields();
        });
    };

    // === WAREHOUSE TABLE ===
    const warehouseColumns = [
        { title: 'Nomi', dataIndex: 'name', key: 'name', render: (v, rec) => (
            <Button type="link" style={{ padding: 0, fontWeight: 600 }} onClick={() => setActiveWarehouse(rec)}>{v}</Button>
        )},
        { title: 'Izoh', dataIndex: 'note', key: 'note', ellipsis: true },
        {
            title: '', key: 'actions', width: 120,
            render: (_, rec) => (
                <Space size={4}>
                    <Button size="small" icon={<AppstoreOutlined />} onClick={() => setActiveWarehouse(rec)}>Ochish</Button>
                    <Button size="small" icon={<EditOutlined />} onClick={() => openEditWarehouse(rec)} />
                    <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDeleteWarehouse(rec)} />
                </Space>
            ),
        },
    ];

    // === ITEM TABLE ===
    const itemColumns = [
        { title: 'Nomi', dataIndex: 'name', key: 'name' },
        {
            title: 'Kategoriya', dataIndex: 'category_name', key: 'category_name',
            render: (v) => v ? <Tag color="blue">{v}</Tag> : <Tag>Umumiy</Tag>,
        },
        {
            title: 'Soni', dataIndex: 'count', key: 'count', width: 90,
            render: (v) => <Text strong>{v || 0}</Text>,
        },
        {
            title: 'Narxi', dataIndex: 'price', key: 'price', width: 130,
            render: (v) => v ? `${Number(v).toLocaleString()} so'm` : '—',
        },
        {
            title: 'Umumiy', key: 'total', width: 140,
            render: (_, rec) => {
                const total = (rec.count || 0) * (rec.price || 0);
                return total ? <Text strong style={{ color: '#52c41a' }}>{total.toLocaleString()} so'm</Text> : '—';
            },
        },
        {
            title: '', key: 'actions', width: 130,
            render: (_, rec) => (
                <Space size={4}>
                    <Button size="small" style={{ color: '#fa8c16', borderColor: '#fa8c16' }} icon={<MinusCircleOutlined />} onClick={() => openWriteoff(rec)} disabled={!rec.count} />
                    <Button size="small" icon={<EditOutlined />} onClick={() => openEditItem(rec)} />
                    <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDeleteItem(rec)} />
                </Space>
            ),
        },
    ];

    // Ombor ichidagi sahifa (items)
    if (activeWarehouse) {
        const totalValue = warehouseItems.reduce((s, i) => s + (i.count || 0) * (i.price || 0), 0);
        const totalCount = warehouseItems.reduce((s, i) => s + (i.count || 0), 0);

        return (
            <div style={{ padding: '16px 0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                    <Button icon={<ArrowLeftOutlined />} onClick={() => setActiveWarehouse(null)}>Orqaga</Button>
                    <Title level={5} style={{ margin: 0 }}>{activeWarehouse.name}</Title>
                </div>

                {/* Stats */}
                <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                    <Card size="small" style={{ flex: 1, borderRadius: 10 }}>
                        <Text type="secondary">Mahsulotlar</Text>
                        <div style={{ fontSize: 20, fontWeight: 700 }}>{warehouseItems.length} ta</div>
                    </Card>
                    <Card size="small" style={{ flex: 1, borderRadius: 10 }}>
                        <Text type="secondary">Jami soni</Text>
                        <div style={{ fontSize: 20, fontWeight: 700 }}>{totalCount.toLocaleString()}</div>
                    </Card>
                    <Card size="small" style={{ flex: 1, borderRadius: 10 }}>
                        <Text type="secondary">Umumiy qiymat</Text>
                        <div style={{ fontSize: 20, fontWeight: 700, color: '#52c41a' }}>{totalValue.toLocaleString()} so'm</div>
                    </Card>
                    <Card size="small" style={{ flex: 1, borderRadius: 10 }}>
                        <Text type="secondary">Kategoriyalar</Text>
                        <div style={{ fontSize: 20, fontWeight: 700 }}>{warehouseCategories.length} ta</div>
                    </Card>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                    <Space>
                        <Button icon={<TagOutlined />} onClick={() => setCatDrawer(true)}>Kategoriyalar</Button>
                        <Button icon={<HistoryOutlined />} onClick={() => setHistoryDrawer(true)}>Spisanie tarixi ({warehouseWriteoffs.length})</Button>
                    </Space>
                    <Button type="primary" icon={<PlusOutlined />} onClick={openAddItem}>Mahsulot qo'shish</Button>
                </div>

                {/* Items table */}
                <Table
                    dataSource={warehouseItems}
                    columns={itemColumns}
                    rowKey="id"
                    size="small"
                    pagination={warehouseItems.length > 20 ? { pageSize: 20 } : false}
                    locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Mahsulotlar yo'q" /> }}
                />

                {/* Item modal */}
                <Modal
                    title={editItem ? "Mahsulotni tahrirlash" : "Yangi mahsulot qo'shish"}
                    open={itemModal}
                    onCancel={() => setItemModal(false)}
                    footer={null}
                    destroyOnClose
                >
                    <Form form={itemForm} layout="vertical" onFinish={handleItemSubmit} requiredMark={false}>
                        <Form.Item name="name" label="Mahsulot nomi" rules={[{ required: true, message: 'Nomini kiriting' }]}>
                            <Input placeholder="Masalan: Stol, Stul, Mikrofon..." size="large" />
                        </Form.Item>
                        <div style={{ display: 'flex', gap: 12 }}>
                            <Form.Item name="count" label="Soni" style={{ flex: 1 }} rules={[{ required: true, message: 'Sonini kiriting' }]}>
                                <InputNumber placeholder="0" size="large" min={0} style={{ width: '100%' }} />
                            </Form.Item>
                            <Form.Item name="price" label="Narxi (so'm)" style={{ flex: 1 }}>
                                <InputNumber placeholder="0" size="large" min={0} style={{ width: '100%' }}
                                    formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                                    parser={v => v.replace(/\s/g, '')}
                                />
                            </Form.Item>
                        </div>
                        <Form.Item name="warehouse_categories_id" label="Kategoriya">
                            <Select
                                placeholder="Kategoriya tanlang"
                                allowClear
                                options={[
                                    { label: 'Umumiy (kategoriyasiz)', value: 0 },
                                    ...warehouseCategories.map(c => ({ label: c.name, value: c.id })),
                                ]}
                            />
                        </Form.Item>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                            <Button onClick={() => setItemModal(false)}>Bekor</Button>
                            <Button type="primary" htmlType="submit">Saqlash</Button>
                        </div>
                    </Form>
                </Modal>

                {/* Categories Drawer */}
                <Drawer
                    title="Kategoriyalar"
                    open={catDrawer}
                    onClose={() => setCatDrawer(false)}
                    width={400}
                    extra={<Button type="primary" size="small" icon={<PlusOutlined />} onClick={openAddCat}>Qo'shish</Button>}
                >
                    {warehouseCategories.length === 0 ? (
                        <Empty description="Kategoriyalar yo'q" />
                    ) : (
                        warehouseCategories.map(cat => (
                            <div key={cat.id} style={{
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                padding: '10px 12px', marginBottom: 8,
                                background: 'var(--qk-bg-soft)', borderRadius: 8, border: '1px solid var(--qk-border)',
                            }}>
                                <Text strong>{cat.name}</Text>
                                <Space size={4}>
                                    <Button size="small" icon={<EditOutlined />} onClick={() => openEditCat(cat)} />
                                    <Popconfirm
                                        title="Kategoriyani o'chirish?"
                                        description="Mahsulotlar 'Umumiy' ga o'tkaziladi"
                                        onConfirm={() => deleteWarehouseCategory(activeWarehouse.id, cat.id)}
                                        okText="O'chirish" cancelText="Bekor"
                                    >
                                        <Button size="small" danger icon={<DeleteOutlined />} />
                                    </Popconfirm>
                                </Space>
                            </div>
                        ))
                    )}
                </Drawer>

                {/* Category modal */}
                <Modal
                    title={editCat ? "Kategoriyani tahrirlash" : "Yangi kategoriya"}
                    open={catModal}
                    onCancel={() => setCatModal(false)}
                    footer={null}
                    destroyOnClose
                >
                    <Form form={catForm} layout="vertical" onFinish={handleCatSubmit} requiredMark={false}>
                        <Form.Item name="name" label="Kategoriya nomi" rules={[{ required: true, message: 'Nomini kiriting' }]}>
                            <Input placeholder="Masalan: Jihozlar, Idishlar..." size="large" />
                        </Form.Item>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                            <Button onClick={() => setCatModal(false)}>Bekor</Button>
                            <Button type="primary" htmlType="submit">Saqlash</Button>
                        </div>
                    </Form>
                </Modal>

                {/* Writeoff modal */}
                <Modal
                    title={<span><MinusCircleOutlined style={{ color: '#fa8c16', marginRight: 8 }} />Mahsulotni spisat qilish</span>}
                    open={writeoffModal}
                    onCancel={() => setWriteoffModal(false)}
                    footer={null}
                    destroyOnClose
                >
                    {writeoffRecord && (
                        <div style={{ background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.4)', borderRadius: 8, padding: '10px 14px', marginBottom: 16 }}>
                            <Text strong>{writeoffRecord.name}</Text>
                            <Text type="secondary" style={{ marginLeft: 12 }}>Omborda: <Text strong style={{ color: '#1890ff' }}>{writeoffRecord.count} dona</Text></Text>
                        </div>
                    )}
                    <Form form={writeoffForm} layout="vertical" onFinish={handleWriteoff} requiredMark={false}>
                        <Form.Item name="count" label="Spisat qilinadigan soni" rules={[
                            { required: true, message: 'Sonini kiriting' },
                            { type: 'number', max: writeoffRecord?.count || 0, message: `Maksimum ${writeoffRecord?.count || 0} dona` },
                            { type: 'number', min: 1, message: 'Kamida 1 dona' },
                        ]}>
                            <InputNumber placeholder="0" size="large" min={1} max={writeoffRecord?.count || 0} style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item name="reason" label="Sabab">
                            <Input.TextArea placeholder="Masalan: singan, eskirgan, yo'qolgan..." rows={3} />
                        </Form.Item>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                            <Button onClick={() => setWriteoffModal(false)}>Bekor</Button>
                            <Button type="primary" danger htmlType="submit" icon={<MinusCircleOutlined />}>Spisat qilish</Button>
                        </div>
                    </Form>
                </Modal>

                {/* Writeoff history drawer */}
                <Drawer
                    title="Spisanie tarixi"
                    open={historyDrawer}
                    onClose={() => setHistoryDrawer(false)}
                    width={520}
                >
                    {warehouseWriteoffs.length === 0 ? (
                        <Empty description="Spisanie tarixi yo'q" />
                    ) : (
                        <Table
                            dataSource={warehouseWriteoffs}
                            rowKey="id"
                            size="small"
                            pagination={warehouseWriteoffs.length > 20 ? { pageSize: 20 } : false}
                            columns={[
                                { title: 'Mahsulot', dataIndex: 'item_name', key: 'item_name', render: (v) => v || <Text type="secondary">o'chirilgan</Text> },
                                { title: 'Soni', dataIndex: 'count', key: 'count', width: 70, render: (v) => <Tag color="orange">−{v}</Tag> },
                                { title: 'Sabab', dataIndex: 'reason', key: 'reason', ellipsis: true, render: (v) => v || '—' },
                                { title: 'Sana', dataIndex: 'created', key: 'created', width: 140, render: (v) => v ? String(v).slice(0, 16).replace('T', ' ') : '—' },
                            ]}
                        />
                    )}
                </Drawer>
            </div>
        );
    }

    // === WAREHOUSE LIST ===
    return (
        <div style={{ padding: '16px 0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <Text strong style={{ fontSize: 16 }}>Omborlar ({warehouse.length})</Text>
                <Button type="primary" icon={<PlusOutlined />} onClick={openAddWarehouse}>Ombor qo'shish</Button>
            </div>

            <Modal
                title={editRecord ? "Omborni tahrirlash" : "Yangi ombor qo'shish"}
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                footer={null}
                destroyOnClose
            >
                <Form form={form} layout="vertical" onFinish={handleWarehouseSubmit} requiredMark={false}>
                    <Form.Item name="name" label="Nomi" rules={[{ required: true, message: 'Nomini kiriting' }]}>
                        <Input placeholder="Masalan: Asosiy ombor" size="large" />
                    </Form.Item>
                    <Form.Item name="note" label="Izoh">
                        <Input.TextArea placeholder="Qo'shimcha ma'lumot" rows={3} />
                    </Form.Item>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                        <Button onClick={() => setModalOpen(false)}>Bekor</Button>
                        <Button type="primary" htmlType="submit">Saqlash</Button>
                    </div>
                </Form>
            </Modal>

            <Table
                dataSource={warehouse}
                columns={warehouseColumns}
                rowKey="id"
                size="small"
                pagination={false}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Omborlar yo'q" /> }}
            />
        </div>
    );
}

export default Warehouse;
